/**
 * The one engagement-band treatment: colour, icon and label.
 *
 * WHY THIS EXISTS
 * `shared/engagement.ts` already settled what a score MEANS — 70 is Hot, 50 is
 * Warm, and a contact with no score has no band at all. What it did not settle
 * is how that band LOOKS, and on 2026-09-02 six surfaces drew the same datum
 * six ways:
 *
 *   ContactCard          coral gradient / clownfish flat-with-border / ocean
 *                        gradient — two chip forms inside one three-branch
 *                        conditional — `font-bold`, hidden when unscored
 *   Contacts table       the same three hues (including the odd warm), but
 *                        `font-semibold`, and `score || 0` so an unscored
 *                        contact was labelled Cool
 *   ContactDetailModal   `bg-muted/40 text-foreground` — no hue at all — at
 *                        `text-sm font-bold px-1.5 py-0` in the header and
 *                        `text-base font-bold px-4 py-2` in the score card
 *   AppShell search      `bg-muted/40`, and TrendingUp for EVERY band, so a
 *                        Cool contact showed a rising arrow; unscored read Cool
 *   ContactSummary       plain muted text, no chip, no colour
 *   Lead Temperature     coral / clownfish / ocean dots, and the donut's legend
 *                        said "Cold" where every other surface says "Cool"
 *
 * So one contact could be a red Hot chip on the card, a grey Hot chip in the
 * modal it opens, and a rising arrow labelled Cool in search — three readings
 * of one number, none of them wrong on its own.
 *
 * WHY THE HUES
 * Engagement is a temperature, and the three hues below are what the card, the
 * table and the dashboard donut had already converged on — coral, clownfish,
 * ocean. The modal's private note argued the band needed no hue because it has
 * a label and an icon; that is defensible in isolation and wrong across an app
 * where the same chip is coloured everywhere else. Colour is the datum here, in
 * the same sense the buyer journey's is (docs/design-system.md §Colour), so it
 * is defined once and used everywhere rather than dropped in three places.
 *
 * FORM
 * The chip form is STAGE_CONFIG's, exactly: `bg-gradient-to-b from-X/15
 * to-X/5 text-X`. One form for all three bands — the flat-with-border warm chip
 * was the odd one out in its own switch statement.
 *
 * The classes are spelled out rather than built from a hue name because
 * Tailwind's JIT scans source text: an interpolated class is never emitted and
 * the colour silently does not exist in a production build.
 */

import { Flame, TrendingDown, TrendingUp } from 'lucide-react';
import { ENGAGEMENT_BAND_LABEL, getEngagementBand, type EngagementBand } from '@shared/engagement';

export interface EngagementConfig {
  /** Chip fill + text. The same form as a buyer-journey stage chip. */
  chip: string;
  /** Text alone, for a label that is not in a chip. */
  text: string;
  /** Solid fill, for a legend dot. */
  dot: string;
  /** For a chart series, which needs a colour value rather than a class. */
  hsl: string;
  /** Flame / rising / falling — the direction, for anyone who cannot see hue. */
  icon: typeof Flame;
  /** Translation key for the band's label, and its English. */
  labelKey: string;
  label: string;
  /** Translation key for the band's score range, and its English, e.g. "Score ≥ 70". */
  rangeKey: string;
  range: string;
}

export const ENGAGEMENT_CONFIG: Record<EngagementBand, EngagementConfig> = {
  hot: {
    chip: 'bg-gradient-to-b from-coral/15 to-coral/5 text-coral',
    text: 'text-coral',
    dot: 'bg-coral',
    hsl: 'hsl(var(--coral))',
    icon: Flame,
    labelKey: 'hotEngagement',
    label: ENGAGEMENT_BAND_LABEL.hot,
    rangeKey: 'scoreHotBand',
    range: 'Score ≥ 70',
  },
  warm: {
    chip: 'bg-gradient-to-b from-clownfish/15 to-clownfish/5 text-clownfish',
    text: 'text-clownfish',
    dot: 'bg-clownfish',
    hsl: 'hsl(var(--clownfish))',
    icon: TrendingUp,
    labelKey: 'warmEngagement',
    label: ENGAGEMENT_BAND_LABEL.warm,
    rangeKey: 'scoreWarmBand',
    range: 'Score 50-69',
  },
  cool: {
    chip: 'bg-gradient-to-b from-ocean/15 to-ocean/5 text-ocean',
    text: 'text-ocean',
    dot: 'bg-ocean',
    hsl: 'hsl(var(--ocean))',
    icon: TrendingDown,
    labelKey: 'coolEngagement',
    label: ENGAGEMENT_BAND_LABEL.cool,
    rangeKey: 'scoreCoolBand',
    range: 'Score < 50',
  },
};

/** Ordered hottest first — the order every legend and stat row shows them in. */
export const ENGAGEMENT_BANDS: EngagementBand[] = ['hot', 'warm', 'cool'];

/**
 * The treatment for a score, or `null` when there is no score.
 *
 * `null` is not "Cool". A contact nobody has scored has not been measured, and
 * the surfaces that coerced `score ?? 0` were asserting a coldness the data
 * never claimed.
 */
export function engagementConfig(score: number | null | undefined): EngagementConfig | null {
  const band = getEngagementBand(score);
  return band ? ENGAGEMENT_CONFIG[band] : null;
}

/** The band's label, translated, falling back to the English in shared. */
export function engagementLabel(
  band: EngagementBand | null,
  t: (key: string, english: string) => string
): string | null {
  if (!band) return null;
  return t(ENGAGEMENT_CONFIG[band].labelKey, ENGAGEMENT_CONFIG[band].label);
}
